/**
 * TrueMarch Tiger — the page side (docs/TIGER_AVATAR.md).
 *
 * The runtime is a plain ES module in public/tiger/ (Crest of Embers loads the
 * same file), so it is imported at run time rather than bundled.
 */

export type Dir = 'down' | 'up' | 'left' | 'right';
export type Expr = 'calm' | 'happy' | 'fierce' | 'surprised';
export type Access = 'free' | 'unlock' | 'premium' | 'limited' | 'event';
export interface TigerConfig {
  schemaVersion: number; species: 'truemarch_tiger';
  colors: Record<string, string>; pattern: string;
  head: Record<string, string | null>; body: Record<string, string | null>; belongings: Record<string, string | null>;
  background: string; expression: Expr;
}
export interface CatalogItem { slot: string; name: string; access: Access; channels?: string[]; fullBody?: boolean; price?: number; unlock?: string; ears?: string; attack?: string; family?: string; colors?: Record<string, string>; pattern?: string }
export interface Catalog {
  defaults: TigerConfig; palette: string[];
  slots: Record<string, { group: string; required?: boolean; label: string }>;
  patterns: { id: string; name: string; family?: string; access?: Access }[];
  furPresets: { id: string; name: string; access: Access; family?: string; colors: Record<string, string> }[];
  backgrounds: { id: string; name: string; access?: Access; family?: string; stops: string[]; image?: string; thumbnail?: string; focus?: [number, number] }[];
  items: Record<string, CatalogItem>;
  accessTiers: Record<Access, string>;
}
export type Loop = 'idle' | 'walk' | 'run';
export type Action = 'attack' | 'punch' | 'kick' | 'jump' | 'hurt' | 'wave' | 'victory';
export interface Box { x: number; y: number; w: number; h: number }
/** A character instance from the runtime's animator (tiger.js createTiger). */
export interface Tiger {
  config: TigerConfig; dir: Dir; base: Loop; speed: number; expr?: Expr; readonly clock: number;
  readonly anim: string; readonly busy: boolean; readonly locked: boolean;
  action: { name: string; id: string; time: number; step: number; clip: { dur: number } } | null;
  setBase(name: Loop): void;
  play(name: Action | string, o?: { step?: number }): string | null;
  stop(): void;
  update(dt: number): void;
  on(ev: string, fn: (ev: string, name: string, clip: string) => void): () => void;
  draw(ctx: CanvasRenderingContext2D, x: number, y: number, scale: number, pixelScale?: number, wholeFrames?: boolean): void;
}
export interface TigerRuntime {
  ENGINE: number;
  LOOPS: Loop[]; ACTIONS: Action[];
  DIRS: Record<Dir, { view: 'front' | 'back' | 'side'; flip: boolean }>;
  createTiger(c: TigerConfig, o?: { dir?: Dir; anim?: string }): Tiger;
  actionBox(c: TigerConfig, view: string, anim: string): Box;
  attackKind(c: TigerConfig): string;
  applyAnimal(c: TigerConfig, id: string | null): TigerConfig;
  randomize(c: TigerConfig, o: { allow?: (id: string) => boolean; keep?: Set<string> }): TigerConfig;
  comboLength(c: TigerConfig): number;
  resolveAction(c: TigerConfig, action: string, step?: number): string;
  loadCatalog(): Promise<Catalog>;
  getCatalog(): Catalog;
  normalize(c: unknown): { config: TigerConfig; problems: string[] };
  equipped(c: TigerConfig): string[];
  SLOT_PATH: Record<string, [string, string]>;
  drawTiger(ctx: CanvasRenderingContext2D, c: TigerConfig, o: { dir?: Dir; anim?: string; time?: number; x?: number; y?: number; scale?: number; pixelScale?: number; expr?: Expr; shadow?: boolean; fx?: boolean }): void;
  drawPortrait(ctx: CanvasRenderingContext2D, c: TigerConfig, size: number, o?: { expr?: Expr; background?: boolean }): void;
  drawBackground(ctx: CanvasRenderingContext2D, id: string, w: number, h: number): void;
  imagesSettled(): Promise<void>;
  preloadBackground(id: string, o?: { thumbnail?: boolean }): Promise<void>;
}

let loading: Promise<TigerRuntime> | null = null;
export function loadTiger(): Promise<TigerRuntime> {
  if (!loading) {
    const url = '/tiger/tiger.js';
    loading = import(/* webpackIgnore: true */ url).then(async (m: TigerRuntime) => { await m.loadCatalog(); return m; });
    loading.catch(() => { loading = null; });
  }
  return loading;
}

const API = process.env.NEXT_PUBLIC_API_BASE ?? '';
export class TigerApiError extends Error {
  constructor(message: string, public status: number, public code?: string, public errors?: { code: string; field: string; message: string }[]) { super(message); }
}
async function call<T>(path: string, init?: { method?: string; json?: unknown }): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API}/api${path}`, {
      credentials: 'include', cache: 'no-store', method: init?.method ?? 'GET',
      headers: init?.json !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: init?.json !== undefined ? JSON.stringify(init.json) : undefined,
    });
  } catch { throw new TigerApiError('Could not reach the server. Check your connection and try again.', 0); }
  let body: any = {};
  try { body = await res.json(); } catch { /* empty */ }
  if (!res.ok) throw new TigerApiError(body?.error || `That did not work (${res.status}).`, res.status, body?.code, body?.errors);
  return body as T;
}
export const fetchMyTiger = () => call<{ config: TigerConfig; saved: boolean; owned: string[]; avatarKey: string | null; usesTigerPortrait: boolean }>('/tiger');
export const saveMyTiger = (config: TigerConfig, portrait: string | undefined, usePortrait: boolean) =>
  call<{ config: TigerConfig; avatarKey: string | null }>('/tiger', { method: 'PUT', json: { config, portrait, usePortrait } });
export const fetchPendingTiger = () => call<{ pending: boolean; config?: TigerConfig }>('/tiger/pending');

/** The 256px profile portrait as a PNG data URL (drawn twice if painted layers were still loading). */
export async function portraitPng(rt: TigerRuntime, config: TigerConfig): Promise<string> {
  const cv = document.createElement('canvas');
  cv.width = cv.height = 256;
  const ctx = cv.getContext('2d')!;
  await rt.preloadBackground(config.background);
  rt.drawPortrait(ctx, config, 256);
  await rt.imagesSettled();
  ctx.clearRect(0, 0, 256, 256);
  rt.drawPortrait(ctx, config, 256);
  return cv.toDataURL('image/png');
}

/** The window event every place showing my own picture listens to: detail { avatarKey }. */
export const AVATAR_EVENT = 'kharma:avatar';
export function announceAvatar(avatarKey: string | null) {
  window.dispatchEvent(new CustomEvent(AVATAR_EVENT, { detail: { avatarKey } }));
}
